/**
 * Splits rows into today's and the rest (spec 141 US5), by the row's newest
 * notification in the reader's local day. Order within each part is kept.
 */
export function splitByDay<T>(
  rows: readonly T[],
  createdAt: (row: T) => string,
  now: Date = new Date(),
): { today: T[]; earlier: T[] } {
  const startOfToday = new Date(now);
  startOfToday.setHours(0, 0, 0, 0);
  const today: T[] = [];
  const earlier: T[] = [];
  for (const row of rows) {
    (new Date(createdAt(row)) >= startOfToday ? today : earlier).push(row);
  }
  return { today, earlier };
}

/** One day's rows on the notifications page, under its heading. */
export interface DayGroup<T> {
  key: string;
  label: string;
  rows: T[];
}

/**
 * Rows under day headings (spec 147 FR-003): Today, Yesterday, then the date,
 * with the year only when it is not this one. Newest-first rows stay in
 * order; a day is one run of them.
 */
export function groupByDay<T>(
  rows: readonly T[],
  createdAt: (row: T) => string,
  now: Date = new Date(),
): DayGroup<T>[] {
  const today = new Date(now);
  today.setHours(0, 0, 0, 0);
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  const groups: DayGroup<T>[] = [];
  for (const row of rows) {
    const day = new Date(createdAt(row));
    day.setHours(0, 0, 0, 0);
    const key = day.toDateString();
    const last = groups.at(-1);
    if (last?.key === key) {
      last.rows.push(row);
      continue;
    }
    const label =
      day.getTime() === today.getTime()
        ? 'Today'
        : day.getTime() === yesterday.getTime()
          ? 'Yesterday'
          : day.toLocaleDateString(undefined, {
              weekday: 'short',
              day: 'numeric',
              month: 'short',
              year:
                day.getFullYear() === today.getFullYear()
                  ? undefined
                  : 'numeric',
            });
    groups.push({ key, label, rows: [row] });
  }
  return groups;
}
